import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const Panel = ({ icon, title, body, children, testId }) => (
  <div data-testid={testId} className="lux-card mx-auto mt-10 max-w-lg p-8 text-center sm:p-10">
    {icon}
    <h1 className="mt-5 text-2xl text-amber-50 sm:text-3xl">{title}</h1>
    <p className="mt-3 text-sm text-zinc-400">{body}</p>
    <div className="mt-8 flex justify-center gap-3">{children}</div>
  </div>
);

const usePoll = (fn, done, deps) => {
  useEffect(() => {
    let n = 0, alive = true;
    const tick = async () => {
      n += 1;
      const r = await fn().catch(() => null);
      if (!alive) return;
      if (!done(r) && n < 30) setTimeout(tick, 2000);
    };
    tick();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export function SetupSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [error, setError] = useState(null);
  const sid = params.get("session_id");
  usePoll(async () => {
    const { data } = await api.get(`/payments/setup-status/${sid}`);
    if (data.verified) { setState("verified"); await refresh(); }
    else if (data.error) { setState("rejected"); setError(data.error); await refresh(); }
    return data.verified || Boolean(data.error);
  }, (v) => v, [sid]);
  if (state === "rejected") {
    return (
      <PortalShell>
        <Panel testId="setup-rejected" icon={<XCircle className="mx-auto h-10 w-10 text-red-400" />} title="Card not accepted" body={error}>
          <Link to="/customer/add-card" data-testid="setup-retry-button" className="btn-gold">Add a different card</Link>
        </Panel>
      </PortalShell>
    );
  }
  return (
    <PortalShell>
      {state === "checking"
        ? <Panel testId="setup-checking" icon={<Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-400" />} title="Verifying your card" body="Confirming your card…" />
        : <Panel testId="setup-verified" icon={<ShieldCheck className="mx-auto h-10 w-10 text-emerald-400" />} title="Card saved" body="Your credit or debit card is on file. Booking is now enabled.">
            <Link to="/customer" data-testid="setup-continue-button" className="btn-gold">Book a ride</Link>
          </Panel>}
    </PortalShell>
  );
}

export function SetupCancel() {
  return (
    <PortalShell>
      <Panel testId="setup-cancelled" icon={<XCircle className="mx-auto h-10 w-10 text-red-400" />} title="Payment method not added" body="Setup was cancelled. Your account remains unverified — add a payment method to book.">
        <Link to="/customer" data-testid="setup-back-button" className="btn-outline-gold">Back to booking</Link>
      </Panel>
    </PortalShell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const sid = params.get("session_id");
  usePoll(async () => {
    const { data } = await api.get(`/payments/status/${sid}`);
    if (data.payment_status === "paid") { setState("paid"); setTimeout(() => nav("/customer"), 1200); return true; }
    if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return true; }
    return false;
  }, (v) => v, [sid]);
  return (
    <PortalShell>
      {state === "checking" && <Panel testId="payment-checking" icon={<Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-400" />} title="Confirming payment" body="We'll request your driver as soon as your payment is confirmed." />}
      {state === "paid" && <Panel testId="payment-paid" icon={<ShieldCheck className="mx-auto h-10 w-10 text-emerald-400" />} title="Payment confirmed" body="Finding your driver…" />}
      {state === "failed" && <Panel testId="payment-failed" icon={<XCircle className="mx-auto h-10 w-10 text-red-400" />} title="Payment failed" body="No driver was requested."><Link to="/customer" className="btn-outline-gold">Back to booking</Link></Panel>}
    </PortalShell>
  );
}

export function PayPalReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const tripId = params.get("trip_id");
  useEffect(() => {
    api.post("/payments/paypal/capture", { trip_id: tripId })
      .then(({ data }) => { if (data.payment_status === "paid") { setState("paid"); setTimeout(() => nav("/customer"), 1200); } else setState("failed"); })
      .catch(() => setState("failed"));
  }, [tripId, nav]);
  return (
    <PortalShell>
      {state === "checking" && <Panel testId="paypal-checking" icon={<Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-400" />} title="Confirming your PayPal payment" body="We'll request your driver as soon as your payment is confirmed." />}
      {state === "paid" && <Panel testId="paypal-paid" icon={<ShieldCheck className="mx-auto h-10 w-10 text-emerald-400" />} title="Payment confirmed" body="Finding your driver…" />}
      {state === "failed" && <Panel testId="paypal-failed" icon={<XCircle className="mx-auto h-10 w-10 text-red-400" />} title="PayPal payment not completed" body="No driver was requested."><Link to="/customer" data-testid="paypal-back-button" className="btn-outline-gold">Back to booking</Link></Panel>}
    </PortalShell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const tripId = params.get("trip_id");
  useEffect(() => { if (tripId) api.post(`/trips/${tripId}/cancel`).catch(() => {}); }, [tripId]);
  return (
    <PortalShell>
      <Panel testId="payment-cancelled" icon={<XCircle className="mx-auto h-10 w-10 text-red-400" />} title="Payment not completed" body="Your card was not charged and no driver was requested.">
        <Link to="/customer" data-testid="payment-cancel-back-button" className="btn-gold">Back to booking</Link>
      </Panel>
    </PortalShell>
  );
}
